import React, { useCallback, useEffect, useState } from 'react';
import { fetchHostingLeaderboard } from "src/actions/mine_craft/hostingActions";
import { useAppDispatch } from "src/store/hooks";
import { formattedDate } from "src/helpers/DateHelpers";

import { Button, Table, Tag } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import Card from "src/componets/Card";
import { playtimeHours, shortFromNow } from "../../Servers/_partials/_serverHelpers";

interface LeaderboardRow {
  name: string;
  xuid: string | null;
  playtimeSeconds: number;
  sessions: number;
  servers: number;
  lastSeen: string;
  online: boolean;
}

const LIMIT = 100;

// Top players by playtime aggregated across every hosted server of the
// platform (crafty holds one API key, so this cannot be app-scoped).
const _Leaderboard = () => {
  const dispatch = useAppDispatch();

  const [players, setPlayers] = useState<LeaderboardRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const resultAction = await dispatch(fetchHostingLeaderboard({ limit: LIMIT }));
    setLoading(false);

    if (fetchHostingLeaderboard.fulfilled.match(resultAction)) {
      setPlayers(resultAction.payload?.players || []);
    }
  }, [dispatch]);

  useEffect(() => {
    load().catch(console.error);
  }, [load]);

  const columns = [
    {
      title: '#',
      key: 'rank',
      width: 56,
      render: (_: unknown, __: LeaderboardRow, index: number) => index + 1
    },
    {
      title: 'Player',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, row: LeaderboardRow) => (
        <span className='o-player'>
          <span className='o-tag'>{name}</span>
          {row.online && <Tag color='green'>online</Tag>}
        </span>
      )
    },
    {
      title: 'Playtime',
      dataIndex: 'playtimeSeconds',
      key: 'playtime',
      render: (seconds: number) => playtimeHours(seconds)
    },
    { title: 'Sessions', dataIndex: 'sessions', key: 'sessions' },
    { title: 'Servers', dataIndex: 'servers', key: 'servers' },
    {
      title: 'Last seen',
      dataIndex: 'lastSeen',
      key: 'lastSeen',
      render: (iso: string) =>
        iso ? <span title={formattedDate(iso).dateTime}>{shortFromNow(iso)}</span> : '-'
    }
  ];

  return (
    <Card className='v-hosting-leaderboard'>
      <div className='o-head'>
        <h4 className='v-content-title'>Top players by playtime</h4>
        <span className='o-count'>{players.length}</span>
        <Button
          className='o-icon-btn'
          icon={<SyncOutlined />}
          title='Refresh'
          aria-label='Refresh leaderboard'
          loading={loading}
          onClick={() => load()}
        />
      </div>

      <Table
        size='small'
        rowKey={row => row.xuid || row.name}
        loading={loading && !players.length}
        columns={columns}
        dataSource={players}
        pagination={{ pageSize: 25, hideOnSinglePage: true, showSizeChanger: false }}
      />
    </Card>
  );
}

export default _Leaderboard;
